import { z } from "zod";

import { Classification } from "@/src/models/constants/classifications";
import { splitDocumentConfidenceSchema } from "@/src/models/file";

export const intakeAnnotationJsonValueSchema = z.json();
export type IntakeAnnotationJsonValue = z.infer<typeof intakeAnnotationJsonValueSchema>;

export const classificationPlanItemSchema = z.object({
  classification: z.nativeEnum(Classification),
  groupIndex: z.number().int().nonnegative(),
  pages: z.array(z.number().int().nonnegative()),
});
export type ClassificationPlanItem = z.infer<typeof classificationPlanItemSchema>;

export const intakeClassificationReasonsSchema = z.record(z.string(), z.string().trim().min(1).max(1000));
export type IntakeClassificationReasons = z.infer<typeof intakeClassificationReasonsSchema>;
export type IntakeClassificationReasonDrafts = Record<string, string>;

export const intakeClassificationSubmissionSchema = z.object({
  taskId: z.string().uuid(),
  splitDict: splitDocumentConfidenceSchema,
  reasons: intakeClassificationReasonsSchema,
});
export type IntakeClassificationSubmission = z.infer<typeof intakeClassificationSubmissionSchema>;

export const SIMPLIFIED_CLASSIFICATION_CHANGE_TYPES = [
  "reclassified",
  "resegmented",
  "reclassified_and_resegmented",
] as const;

export const simplifiedClassificationChangeTypeSchema = z.enum(SIMPLIFIED_CLASSIFICATION_CHANGE_TYPES);
export type SimplifiedClassificationChangeType = z.infer<typeof simplifiedClassificationChangeTypeSchema>;

export const justifiedClassificationChangeSchema = z.object({
  key: z.string(),
  type: simplifiedClassificationChangeTypeSchema,
  beforeItems: z.array(classificationPlanItemSchema),
  afterItems: z.array(classificationPlanItemSchema),
  affectedPages: z.array(z.number().int().nonnegative()),
  reasonText: z.string().trim().min(1).max(1000),
});
export type JustifiedClassificationChange = z.infer<typeof justifiedClassificationChangeSchema>;

export const intakeClassificationAnnotationSchema = z.object({
  groupId: z.string().uuid(),
  taskId: z.string().uuid(),
  reviewerId: z.string().uuid(),
  beforeValue: z.array(classificationPlanItemSchema),
  afterValue: z.array(classificationPlanItemSchema),
  classificationChanges: z.array(justifiedClassificationChangeSchema),
});
export type IntakeClassificationAnnotation = z.infer<typeof intakeClassificationAnnotationSchema>;

export const INTAKE_EXTRACTION_REASON_CODES = [
  "extraction_typo",
  "extraction_confused_for_different_entity",
  "incorrect_match",
  "geocoding_new_address",
  "formatting",
  "incorrect_value_customer_requirement",
  "value_not_extracted",
  "value_missing_from_document",
  "not_relevant",
  "insignificant",
] as const;

export const intakeExtractionReasonCodeSchema = z.enum(INTAKE_EXTRACTION_REASON_CODES);
export type IntakeExtractionReasonCode = z.infer<typeof intakeExtractionReasonCodeSchema>;
export type IntakeExtractionReasonVariant = "field" | "match" | "locationMatch";

export const INTAKE_EXTRACTION_REASON_LABELS: Record<IntakeExtractionReasonCode, string> = {
  extraction_typo: "Incorrect extraction (typo)",
  extraction_confused_for_different_entity: "Confused for different entity in document",
  incorrect_match: "Incorrect match",
  geocoding_new_address: "Geocoding (new address)",
  formatting: "Formatting",
  incorrect_value_customer_requirement: "Incorrect value (customer requirement)",
  value_not_extracted: "Value not extracted",
  value_missing_from_document: "Value missing from document",
  not_relevant: "Not relevant",
  insignificant: "Insignificant (efficiency)",
};

export const intakeExtractionAnnotationDraftSchema = z.object({
  beforeValue: intakeAnnotationJsonValueSchema,
  beforeValueSource: z.enum(["extracted", "existing_entity"]),
  beforeBlockPresent: z.boolean(),
  afterValue: intakeAnnotationJsonValueSchema,
  afterBlockPresent: z.boolean(),
  reasonCode: intakeExtractionReasonCodeSchema.optional(),
  reasonText: z.string().trim().max(1000).optional(),
});
export type IntakeExtractionAnnotationDraft = z.infer<typeof intakeExtractionAnnotationDraftSchema>;

export const intakeExtractionAnnotationDraftsSchema = z.record(z.string(), intakeExtractionAnnotationDraftSchema);
export type IntakeExtractionAnnotationDrafts = z.infer<typeof intakeExtractionAnnotationDraftsSchema>;

export const intakeExtractionAnnotationMetadataSchema = intakeExtractionAnnotationDraftSchema
  .pick({
    beforeValue: true,
    reasonCode: true,
    reasonText: true,
  })
  .strict();
export type IntakeExtractionAnnotationMetadata = z.infer<typeof intakeExtractionAnnotationMetadataSchema>;

export const intakeExtractionAnnotationsMetadataSchema = z.record(z.string(), intakeExtractionAnnotationMetadataSchema);
export type IntakeExtractionAnnotationsMetadata = z.infer<typeof intakeExtractionAnnotationsMetadataSchema>;

export const intakeExtractionAnnotationSchema = z.object({
  sectionId: z.string().uuid(),
  fieldPath: z.string().min(1),
  beforeValue: intakeAnnotationJsonValueSchema,
  afterValue: intakeAnnotationJsonValueSchema,
  reasonCode: intakeExtractionReasonCodeSchema.nullable(),
  reasonText: z.string().trim().max(1000).nullable(),
});
export type IntakeExtractionAnnotation = z.infer<typeof intakeExtractionAnnotationSchema>;

export const intakeExtractionAnnotationBatchSchema = z.object({
  groupId: z.string().uuid(),
  taskId: z.string().uuid(),
  reviewerId: z.string().uuid(),
  annotations: z.array(intakeExtractionAnnotationSchema),
});
export type IntakeExtractionAnnotationBatch = z.infer<typeof intakeExtractionAnnotationBatchSchema>;
